const STORAGE_KEY = 'theme_color';
const SIDEBAR_BASE = [15, 23, 42];

const PALETTES = {
  blue: {
    50: '239 246 255',
    100: '219 234 254',
    200: '191 219 254',
    300: '147 197 253',
    400: '96 165 250',
    500: '59 130 246',
    600: '37 99 235',
    700: '29 78 216',
    800: '30 64 175',
    900: '30 58 138',
  },
  indigo: {
    50: '238 242 255',
    100: '224 231 255',
    200: '199 210 254',
    300: '165 180 252',
    400: '129 140 248',
    500: '99 102 241',
    600: '79 70 229',
    700: '67 56 202',
    800: '55 48 163',
    900: '49 46 129',
  },
  violet: {
    50: '245 243 255',
    100: '237 233 254',
    200: '221 214 254',
    300: '196 181 253',
    400: '167 139 250',
    500: '139 92 246',
    600: '124 58 237',
    700: '109 40 217',
    800: '91 33 182',
    900: '76 29 149',
  },
  emerald: {
    50: '236 253 245',
    100: '209 250 229',
    200: '167 243 208',
    300: '110 231 183',
    400: '52 211 153',
    500: '16 185 129',
    600: '5 150 105',
    700: '4 120 87',
    800: '6 95 70',
    900: '6 78 59',
  },
  teal: {
    50: '240 253 250',
    100: '204 251 241',
    200: '153 246 228',
    300: '94 234 212',
    400: '45 212 191',
    500: '20 184 166',
    600: '13 148 136',
    700: '15 118 110',
    800: '17 94 89',
    900: '19 78 74',
  },
  cyan: {
    50: '236 254 255',
    100: '207 250 254',
    200: '165 243 252',
    300: '103 232 249',
    400: '34 211 238',
    500: '6 182 212',
    600: '8 145 178',
    700: '14 116 144',
    800: '21 94 117',
    900: '22 78 99',
  },
  amber: {
    50: '255 251 235',
    100: '254 243 199',
    200: '253 230 138',
    300: '252 211 77',
    400: '251 191 36',
    500: '245 158 11',
    600: '217 119 6',
    700: '180 83 9',
    800: '146 64 14',
    900: '120 53 15',
  },
  orange: {
    50: '255 247 237',
    100: '255 237 213',
    200: '254 215 170',
    300: '253 186 116',
    400: '251 146 60',
    500: '249 115 22',
    600: '234 88 12',
    700: '194 65 12',
    800: '154 52 18',
    900: '124 45 18',
  },
  rose: {
    50: '255 241 242',
    100: '255 228 230',
    200: '254 205 211',
    300: '253 164 175',
    400: '251 113 133',
    500: '244 63 94',
    600: '225 29 72',
    700: '190 18 60',
    800: '159 18 57',
    900: '136 19 55',
  },
};

export const THEME_COLOR_OPTIONS = [
  { key: 'blue', label: 'Blau', swatch: PALETTES.blue[600] },
  { key: 'indigo', label: 'Indigo', swatch: PALETTES.indigo[600] },
  { key: 'violet', label: 'Violett', swatch: PALETTES.violet[600] },
  { key: 'emerald', label: 'Smaragd', swatch: PALETTES.emerald[600] },
  { key: 'teal', label: 'Teal', swatch: PALETTES.teal[600] },
  { key: 'cyan', label: 'Cyan', swatch: PALETTES.cyan[600] },
  { key: 'amber', label: 'Amber', swatch: PALETTES.amber[600] },
  { key: 'orange', label: 'Orange', swatch: PALETTES.orange[600] },
  { key: 'rose', label: 'Rose', swatch: PALETTES.rose[600] },
];

export function resolveThemeColor(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return PALETTES[normalized] ? normalized : 'blue';
}

function parseRgbTriplet(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part))
    .slice(0, 3);
}

function mixRgb(primary, secondary, primaryWeight = 0.58) {
  const p = parseRgbTriplet(primary);
  if (p.length !== 3) return secondary.join(' ');
  const s = secondary;
  const w = Math.max(0, Math.min(1, Number(primaryWeight) || 0.58));
  return p.map((value, index) => Math.round(value * w + s[index] * (1 - w))).join(' ');
}

export function applyThemeColor(value, persist = true) {
  if (typeof document === 'undefined') return;
  const key = resolveThemeColor(value);
  const palette = PALETTES[key];
  const root = document.documentElement;
  const shades = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];
  for (const shade of shades) {
    root.style.setProperty(`--accent-${shade}-rgb`, palette[shade]);
  }
  root.style.setProperty('--color-accent', `rgb(${palette[600]})`);
  root.style.setProperty('--color-accent-hover', `rgb(${palette[700]})`);
  root.style.setProperty('--sidebar-bg-rgb', mixRgb(palette[900], SIDEBAR_BASE, 0.58));
  if (persist && typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {}
  }
}

export function initThemeFromStorage() {
  if (typeof localStorage === 'undefined') return;
  let stored = 'blue';
  try {
    stored = localStorage.getItem(STORAGE_KEY) || 'blue';
  } catch {}
  applyThemeColor(stored, false);
}
